import React, {FC, useEffect, useMemo} from "react";
import {useTranslation} from "react-i18next";
import {SaveOutlined} from "@ant-design/icons";
import {Alert, App, Button, Col, Form, Row, Skeleton} from "antd";
import {
    EventTaxSettingReturnDto,
    useCreateEventTaxSettingMutation,
    useEventTaxSettingsListQuery,
    useUpdateEventTaxSettingMutation
} from "../../../GraphQL/generated/graphql";
import {Panel, PercentInput} from "../../../Components/ui";
import {getUserFacingErrorMessage} from "../../../userFacingErrorUtils";
import {
    buildEventTaxSettingInput,
    defaultTaxSettingFormValues,
    EventTaxSettingFormValues,
    eventTaxSettingToFormValues,
    requiredRule
} from "./eventConfigPayloads";
import {EventConfigScope} from "./useEventConfigScope";

export const EventTaxPanel: FC<{scope: EventConfigScope}> = ({scope: {eventNumericId, errorLabels, baseWhere}}) => {
    const {message} = App.useApp();
    const {t} = useTranslation();
    const [taxSettingsForm] = Form.useForm<EventTaxSettingFormValues>();
    const taxQuery = useEventTaxSettingsListQuery({variables: {take: 50, where: baseWhere()}});
    const [createTax, {loading: creatingTax}] = useCreateEventTaxSettingMutation();
    const [updateTax, {loading: updatingTax}] = useUpdateEventTaxSettingMutation();

    const selectedTaxSetting = useMemo(
        () => (taxQuery.data?.eventTaxSettings?.items ?? [])
            .find((item): item is EventTaxSettingReturnDto => !!item) ?? null,
        [taxQuery.data?.eventTaxSettings?.items]
    );
    const savingTaxSettings = creatingTax || updatingTax;

    useEffect(() => {
        if (!eventNumericId || taxQuery.loading) {
            return;
        }

        taxSettingsForm.setFieldsValue(eventTaxSettingToFormValues(selectedTaxSetting));
    }, [eventNumericId, selectedTaxSetting, taxQuery.loading, taxSettingsForm]);

    const handleSaveTaxSettings = async () => {
        if (!eventNumericId) {
            return;
        }

        const values = await taxSettingsForm.validateFields();
        const input = buildEventTaxSettingInput(eventNumericId, values);

        try {
            if (selectedTaxSetting?.id) {
                await updateTax({variables: {input: {...input, id: selectedTaxSetting.id}}});
            } else {
                await createTax({variables: {input}});
            }

            message.success(t("event-tax-settings-saved", {defaultValue: "Tax settings saved"}));
            await taxQuery.refetch();
        } catch (error: any) {
            message.error(getUserFacingErrorMessage(error, t, errorLabels));
        }
    };

    return (
        <Panel
            title={t("event-config-operator-tax", {defaultValue: "Fee and accommodation tax"})}
            extra={(
                <Button
                    type="primary"
                    icon={<SaveOutlined/>}
                    disabled={!eventNumericId || taxQuery.loading}
                    loading={savingTaxSettings}
                    onClick={handleSaveTaxSettings}
                >
                    {t("save", {defaultValue: "Save"})}
                </Button>
            )}
        >
            {!!eventNumericId && taxQuery.loading && !selectedTaxSetting ? <Skeleton active/> : (
                <>
                    {taxQuery.error && (
                        <Alert
                            type="error"
                            showIcon
                            style={{marginBottom: 16}}
                            message={t("error-loading-items", {defaultValue: "Error loading items"})}
                            description={getUserFacingErrorMessage(taxQuery.error, t, errorLabels)}
                        />
                    )}
                    <Form
                        form={taxSettingsForm}
                        layout="vertical"
                        disabled={!eventNumericId || taxQuery.loading || savingTaxSettings}
                        initialValues={defaultTaxSettingFormValues()}
                    >
                        <Row gutter={16}>
                            <Col xs={24} md={12}>
                                <Form.Item
                                    label={t("registration-vat", {defaultValue: "Participation and activity VAT"})}
                                    required
                                >
                                    <Form.Item
                                        name="registrationVatPercent"
                                        noStyle
                                        rules={[requiredRule(t("registration-vat-required", {defaultValue: "Registration VAT is required."}))]}
                                    >
                                        <PercentInput/>
                                    </Form.Item>
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12}>
                                <Form.Item
                                    label={t("accommodation-ppo", {defaultValue: "Accommodation PPO"})}
                                    required
                                >
                                    <Form.Item
                                        name="accommodationPpoPercent"
                                        noStyle
                                        rules={[requiredRule(t("accommodation-ppo-required", {defaultValue: "Accommodation PPO is required."}))]}
                                    >
                                        <PercentInput/>
                                    </Form.Item>
                                </Form.Item>
                            </Col>
                        </Row>
                    </Form>
                </>
            )}
        </Panel>
    );
};
